import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import FilterChips from 'components/shared/FilterChips';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import { TabType } from 'components/Tabs';
import { SENDING_GROUP } from 'constants/filterKeysConstants';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import { useReferralsInsightsActions, useReferralsInsightsStore } from 'stores/referralsInsightsStore';

import { filtersForGroupType } from '../filters';

import ReferralsFilterBar from './filterBar/ReferralsFilterBar';
import { VIEW_BY } from './constants';
import { useReferralsInsightsContext } from './ReferralsContainer';

export default function ReferralsPage() {
  const { sendingGroupType, receivingGroupTypes, onTabChange } = useReferralsInsightsContext();
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const filters = useReferralsInsightsStore((state) => state.filters);
  const { setViewBy } = useReferralsInsightsActions();

  const tabs = useMemo<TabType[]>(
    () =>
      receivingGroupTypes
        .filter((gt) => gt.id)
        .map((gt) => ({
          label: `${sendingGroupType?.displayName ?? ''} to ${gt.displayName}`,
          value: gt.id,
        })),
    [receivingGroupTypes, sendingGroupType?.displayName]
  );

  const selectedTab = useMemo(
    () => tabs.find((t) => t.value === selectedReceivingGroupType?.id),
    [tabs, selectedReceivingGroupType]
  );

  const activeFilters = useMemo(
    () => (selectedReceivingGroupType ? filtersForGroupType(filters, selectedReceivingGroupType, SENDING_GROUP) : {}),
    [filters, selectedReceivingGroupType]
  );

  const chippableFilters = Object.entries(activeFilters).reduce((acc, [key, value]) => {
    if (value instanceof FilterValueArray && value.length > 0) {
      return { ...acc, [key]: value };
    }
    return acc;
  }, {});

  const handleTabClick = (tab: TabType) => {
    const match = receivingGroupTypes.find((gt) => gt.id === tab.value);
    if (match) {
      onTabChange(match);
      setViewBy(VIEW_BY.SENDING_GROUP);
    }
  };

  return (
    <Page>
      <StyledPageHeader>
        <ReferralsFilterBar tabs={tabs} selectedTab={selectedTab} onTabClick={handleTabClick} />
      </StyledPageHeader>
      <Page.SubHeader>
        <DesktopOnly>
          <FilterChips filters={chippableFilters} useStore={useReferralsInsightsStore} />
        </DesktopOnly>
      </Page.SubHeader>
      <Page.Content>
        <Outlet />
      </Page.Content>
    </Page>
  );
}

const StyledPageHeader = styled(Page.Header)`
  background: none;
  border-bottom: none;
`;
